/**
 * TeacherApply — application form for teacher review privileges.
 *
 * Three gates (per Lead's brief):
 *   1. Verified school email at recognized academic domain
 *   2. Age 18 or older (DOB collected, age computed server-side)
 *   3. Click verification link sent to the school inbox
 *
 * Empty / pending state is a statement, not an apology.
 */
import { useEffect, useState } from 'react';
import { GraduationCap, ShieldCheck, Clock, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function TeacherApply() {
  const [status, setStatus] = useState(null);
  const [credibility, setCredibility] = useState(0);
  const [schoolEmail, setSchoolEmail] = useState('');
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [resp, setResp] = useState(null);

  const reload = async () => {
    try {
      const r = await api.get('/teacher/status');
      setStatus(r.data);
      setCredibility(r.data?.credibility_score || 0);
    } catch {
      setStatus(null);
    }
  };
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg('');
    setResp(null);
    if (!schoolEmail || !dob) { setMsg('School email and date of birth required.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/teacher/apply', { school_email: schoolEmail, date_of_birth: dob });
      setResp(r.data);
      await reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Submission failed.');
    }
    setBusy(false);
  };

  const isVerified = status?.role === 'admin' || status?.teacher_status === 'verified';
  const isPending = status?.teacher_status === 'pending' || status?.teacher_status === 'pending_admin_review';
  const isDenied = status?.teacher_status === 'denied';

  return (
    <div className="p-5 md:p-7 max-w-3xl" data-testid="teacher-apply-page">
      <div className="flex items-center gap-2 mb-2">
        <GraduationCap size={16} className="text-[#D4A84A]" />
        <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">Teacher Verification</span>
      </div>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-4">
        Apply for Teacher Status
      </h2>

      {isVerified && (
        <div className="border border-[#2F8A92]/30 rounded p-5 mb-6 bg-[#2F8A92]/5" data-testid="teacher-verified-state">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={16} className="text-[#2F8A92]" />
            <span className="font-hud text-[10px] tracking-widest text-[#2F8A92] uppercase">Verified</span>
          </div>
          <p className="font-mono text-sm text-white/85 leading-relaxed">
            You have teacher review privileges. Cast Approve or Fail votes from the chain pages. A Fail must be paired with a sourced contribution — that's how the methodology stays honest.
          </p>
          <p className="font-mono text-xs text-white/65 mt-3">
            Credibility score: <strong className="text-[#D4A84A]">{credibility}</strong> · Earned through methodology-aligned contributions that passed Lead review.
          </p>
        </div>
      )}

      {!isVerified && isPending && (
        <div className="border border-[#D4A84A]/30 rounded p-5 mb-6 bg-[#D4A84A]/5" data-testid="teacher-pending-state">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} className="text-[#D4A84A]" />
            <span className="font-hud text-[10px] tracking-widest text-[#D4A84A] uppercase">
              {status?.teacher_status === 'pending' ? 'Awaiting Email Verification' : 'Awaiting Admin Review'}
            </span>
          </div>
          <p className="font-mono text-sm text-white/85 leading-relaxed">
            {status?.teacher_status === 'pending' ? (
              <>We sent a verification link to <strong className="text-white">{status?.teacher_school_email}</strong>. Click it from your school inbox to activate teacher privileges. The link expires in 24 hours.</>
            ) : (
              <>Your school domain isn't on our recognized list yet. We'll review the application by hand and email you within 2 business days. Edge cases get handled by a human, not an algorithm.</>
            )}
          </p>
        </div>
      )}

      {isDenied && (
        <div className="border border-[#FF6B6B]/30 rounded p-5 mb-6 bg-[#FF6B6B]/5" data-testid="teacher-denied-state">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-[#FF6B6B]" />
            <span className="font-hud text-[10px] tracking-widest text-[#FF6B6B] uppercase">Application Declined</span>
          </div>
          <p className="font-mono text-sm text-white/85 leading-relaxed">
            We weren't able to verify the institution. You retain full access to TSOGN content as a registered user — just not the Approve/Fail review surface.
          </p>
        </div>
      )}

      {!isVerified && !isPending && !isDenied && (
        <>
          <div className="rounded p-4 mb-5 border border-[#2F8A92]/15 bg-[#0A0A0A]">
            <ul className="font-mono text-xs md:text-sm text-white/85 space-y-1.5">
              <li className="flex items-start gap-2"><ShieldCheck size={13} className="text-[#2F8A92] mt-0.5 flex-shrink-0" /> School email at a recognized academic domain (.edu, .ac.uk, .edu.au, .ac.jp, etc.). Edge cases reviewed by hand.</li>
              <li className="flex items-start gap-2"><ShieldCheck size={13} className="text-[#2F8A92] mt-0.5 flex-shrink-0" /> Age 18 or older. No exceptions.</li>
              <li className="flex items-start gap-2"><ShieldCheck size={13} className="text-[#2F8A92] mt-0.5 flex-shrink-0" /> A Fail vote requires a sourced contribution. The accountability scale is public.</li>
            </ul>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="teacher-apply-form">
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">School email</label>
              <input
                type="email"
                value={schoolEmail}
                onChange={(e) => setSchoolEmail(e.target.value)}
                placeholder="you@school.edu"
                data-testid="teacher-apply-school-email"
                className="w-full bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
                required
              />
            </div>
            <div>
              <label className="font-hud text-[10px] tracking-widest text-white/65 uppercase block mb-1">Date of birth</label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                data-testid="teacher-apply-dob"
                className="bg-transparent border border-[#2F8A92]/25 rounded px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#2F8A92]/55"
                required
              />
            </div>
            {msg && <div className="font-mono text-xs text-[#FF6B6B]" data-testid="teacher-apply-error">{msg}</div>}
            {resp?.message && (
              <div className="font-mono text-xs md:text-sm text-white/85 border border-[#2F8A92]/25 rounded p-3 bg-[#2F8A92]/5" data-testid="teacher-apply-result">
                {resp.message}
              </div>
            )}
            {resp?.verify_link_for_dev && (
              <div className="font-mono text-[10px] text-white/55 break-all">
                Dev: <a href={resp.verify_link_for_dev} className="text-[#2F8A92] underline">{resp.verify_link_for_dev}</a>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="teacher-apply-submit"
              className="font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <GraduationCap size={13} />}
              SUBMIT APPLICATION
            </button>
          </form>
        </>
      )}
    </div>
  );
}
